const MAX_BONUS = 1000;
const MAX_TIME = 30;
const FOUND_ITEM_POINTS = 600;
const MAX_GAMES = 12;
const ITEM_TOTAL = 5;
const HOLD_DURATION = 800;
import { clamp } from "@/assets/js/math";
import { Howl } from "howler";

const bgTrack = new Howl({
  src: [require("@/assets/sounds/loop_fade.mp3")],
});
bgTrack.volume(0.3);
bgTrack.loop(true);

const soundCorrect = new Howl({
  src: [require("@/assets/sounds/answer_correct.mp3")],
});
const soundIncorrect = new Howl({
  src: [require("@/assets/sounds/answer_incorrect.mp3")],
});
const soundHintUsed = new Howl({
  src: [require("@/assets/sounds/hint_used.mp3")],
});
const soundLast10Seconds = new Howl({
  src: [require("@/assets/sounds/last_ten_seconds_timer.mp3")],
});
const soundLose = new Howl({
  src: [require("@/assets/sounds/over_lose.mp3")],
});
const soundWin = new Howl({
  src: [require("@/assets/sounds/over_win.mp3")],
});

function shuffle(a: number[]) {
  let j, x, i;
  for (i = a.length - 1; i > 0; i--) {
    j = Math.floor(Math.random() * (i + 1));
    x = a[i];
    a[i] = a[j];
    a[j] = x;
  }
  return a;
}

export default class {
  public timeLeft = MAX_TIME;
  public hintsUsed = 0;
  public foundItems: number[] = [];
  public foundItemsHints: number[] = [];
  public lastTime = -1;
  public hasRun = false;
  public points = 0;
  public percentage = 0;
  public round = 0;
  public game = 0;
  public bonusPoints = MAX_BONUS;
  public gameCompleted = false;
  public rounds: number[] = [];
  public paused = false;
  public hold = false;
  public hasLoadedLeft = false;
  public hasLoadedRight = false;
  public isLast10 = false;
  public holds = 0;
  public started = Date.now();
  public initials = "";
  public submitted = false;

  constructor() {
    this.reset();
  }

  playBG() {
    bgTrack.play();
  }

  pauseBG() {
    bgTrack.pause();
  }

  reset() {
    this.timeLeft = MAX_TIME;
    this.hintsUsed = 0;
    this.foundItems = [];
    this.foundItemsHints = [];
    this.lastTime = -1;
    this.hasRun = false;
    this.points = 0;
    this.percentage = 0;
    this.round = 0;
    this.bonusPoints = MAX_BONUS;
    this.gameCompleted = false;
    this.paused = false;
    this.hold = false;
    this.hasLoadedLeft = false;
    this.hasLoadedRight = false;
    this.isLast10 = false;
    this.holds = 0;
    this.initGames();
    this.game = this.setGame();
  }

  nextGame() {
    this.round++;
    soundWin.play();
    if (this.round >= MAX_GAMES) {
      this.stopLastSeconds();
      this.points += this.addBonus();
      this.gameCompleted = true;
    } else {
      this.timeLeft = MAX_TIME;
      this.foundItems = [];
      this.foundItemsHints = [];
      this.points += this.addBonus();
      this.percentage = 0;
      this.bonusPoints = MAX_BONUS;
      this.hasLoadedLeft = false;
      this.hasLoadedRight = false;
      this.isLast10 = false;
      this.holds = 0;
      this.game = this.setGame();
    }
  }

  private playLastSeconds() {
    if (this.isLast10) {
      soundLast10Seconds.play();
    }
  }

  private pauseLastSeconds() {
    if (this.isLast10) {
      soundLast10Seconds.pause();
    }
  }

  private stopLastSeconds() {
    if (this.isLast10) {
      soundLast10Seconds.stop();
    }
  }

  continue() {
    if (this.paused) {
      this.paused = false;
      this.playLastSeconds();
    } else {
      this.reset();
      window.requestAnimationFrame(this.update.bind(this));
    }
  }

  pause() {
    this.paused = true;
    this.pauseLastSeconds();
  }

  setLoadedImage(isLeft: boolean) {
    if (isLeft) {
      this.hasLoadedLeft = true;
    } else {
      this.hasLoadedRight = true;
    }
  }

  showHint() {
    if (this.hasFoundAll() || this.hintsUsed >= 3 || this.isSoftLocked())
      return;
    this.hintsUsed++;
    this.foundItemHint(this.getNextID());
  }

  missedItem() {
    if (this.isLocked()) return;
    soundIncorrect.play();
    this.timeLeft -= MAX_TIME / 10;
    this.percentage = clamp(1 - this.timeLeft / MAX_TIME, 0, 1);
  }

  foundItem(id: number) {
    if (this.isSoftLocked()) return;
    soundCorrect.play();
    this.foundItems.push(id);
    this.points += FOUND_ITEM_POINTS;
    this.holdFn(() => {
      this.checkGame();
    });
  }

  private isLocked(): boolean {
    return (
      this.paused ||
      !this.hasRun ||
      this.notLoaded() ||
      this.hold ||
      this.gameCompleted
    );
  }

  private isSoftLocked(): boolean {
    return this.paused || !this.hasRun || this.notLoaded();
  }

  private initGames() {
    this.rounds = [];
    for (let index = 0; index < MAX_GAMES; index++) {
      this.rounds.push(index);
    }
    shuffle(this.rounds);
  }

  private setGame() {
    return this.rounds[this.round];
  }

  private update(timestamp: number) {
    if (this.isLocked()) {
      this.lastTime = timestamp;
      this.hasRun = true;
      window.requestAnimationFrame(this.update.bind(this));
    } else {
      if (this.hasRun) {
        const dt = (timestamp - this.lastTime) * 0.001;
        this.timeLeft -= dt;
        this.percentage = clamp(1 - this.timeLeft / MAX_TIME, 0, 1);
        this.bonusPoints = this.calculateBonusPoints(1 - this.percentage);
        if (this.timeLeft < 10 && !this.isLast10) {
          this.isLast10 = true;
          this.playLastSeconds();
        }
      }
      this.lastTime = timestamp;
      this.hasRun = true;
      if (this.percentage !== 1) {
        window.requestAnimationFrame(this.update.bind(this));
      } else {
        this.complete();
      }
    }
  }

  private getNextID(): number {
    for (let index = 0; index <= MAX_GAMES; index++) {
      if (
        !this.foundItems.includes(index) &&
        !this.foundItemsHints.includes(index)
      ) {
        return index;
      }
    }
    return -1;
  }

  private complete() {
    if (this.isLocked()) return;
    this.gameCompleted = true;
    this.stopLastSeconds();
    soundLose.play();
  }

  private notLoaded() {
    return !(this.hasLoadedLeft && this.hasLoadedRight);
  }

  private foundItemHint(id: number) {
    soundHintUsed.play();
    this.foundItemsHints.push(id);
    this.points += FOUND_ITEM_POINTS;
    this.holdFn(() => {
      this.checkGame();
    });
  }

  private checkGame() {
    if (this.hasFoundAll() && this.holds === 0) {
      this.stopLastSeconds();
      this.nextGame();
    }
  }

  private holdFn(cb: () => void) {
    this.hold = true;
    this.holds++;
    this.pauseLastSeconds();
    setTimeout(() => {
      this.holds--;
      if (this.holds === 0) {
        this.playLastSeconds();
        this.hold = false;
      }
      cb();
    }, this.holdDuration());
  }

  private holdDuration(): number {
    return this.hasFoundAll() ? HOLD_DURATION * 2 : HOLD_DURATION;
  }

  private hasFoundAll() {
    return this.foundItems.length + this.foundItemsHints.length === ITEM_TOTAL;
  }

  private calculateBonusPoints(percentage: number): number {
    const minPoints = 100;
    const bonusTimerPoints = (MAX_BONUS - minPoints) * percentage;
    return Math.floor(minPoints + bonusTimerPoints);
  }

  private addBonus() {
    const hintPoints = (3 - this.hintsUsed) * 1000;
    return this.calculateBonusPoints(this.percentage - 1) + hintPoints;
  }
}
